import { API_BASE_URL } from '../config/api';
import { parseNewsListMeta } from './cmsNews';

/** Haftalık toplantı — lacivert (legend ile uyumlu) */
export const FC_MEETING_BG = '#0E4D94';
export const FC_MEETING_BORDER = '#0a3d78';

/** Kulüp etkinliği — turuncu */
export const FC_EVENT_BG = '#e87722';
export const FC_EVENT_BORDER = '#c45a12';

const PAGE_SIZE = 500;
const MAX_PAGES = 50;

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<{ list: unknown[], metaData: unknown }>}
 */
async function parseListResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    const raw = /** @type {{ data?: unknown, metaData?: unknown }} */ (obj)
        .data;
    const list = Array.isArray(raw) ? raw : [];
    const metaData =
        /** @type {{ metaData?: unknown }} */ (obj).metaData ?? null;

    return { list, metaData };
}

/**
 * @param {string} accessToken
 * @param {string} pathWithLeadingSlash e.g. `/clubevents/ClubEvent/GetAll`
 * @param {string} failLabel
 */
async function fetchAllAuthPages(accessToken, pathWithLeadingSlash, failLabel) {
    /** @type {unknown[]} */
    const out = [];

    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const params = new URLSearchParams({
            pageNumber: String(page),
            pageSize: String(PAGE_SIZE),
        });
        const url = `${API_BASE_URL}${pathWithLeadingSlash}?${params}`;
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });

        const { list, metaData } = await parseListResponse(res, failLabel);
        out.push(...list);

        const meta = parseNewsListMeta(
            metaData,
            list.length,
            page,
            PAGE_SIZE,
        );
        if (!meta.hasNext || list.length === 0) break;
    }

    return out;
}

/**
 * @param {unknown} row
 * @returns {Record<string, unknown> | null}
 */
export function mapClubEventToFullCalendar(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    if (r.isActive === false) return null;

    const id = String(r.id ?? '');
    const start = r.startDate;
    const end = r.endDate ?? r.startDate;
    if (!start || typeof start !== 'string') return null;

    return {
        id: `club-${id}`,
        title: String(r.title || 'Etkinlik'),
        start,
        end: typeof end === 'string' ? end : start,
        backgroundColor: FC_EVENT_BG,
        borderColor: FC_EVENT_BORDER,
        textColor: '#ffffff',
        extendedProps: {
            kind: 'event',
            city: r.city != null ? String(r.city) : '',
            source: 'club-event',
            sourceId: id,
        },
    };
}

/**
 * @param {unknown} row
 * @returns {Record<string, unknown> | null}
 */
export function mapMeetingToFullCalendar(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    if (r.isActive === false) return null;

    const id = String(r.id ?? '');
    const start = r.meetingDate;
    if (!start || typeof start !== 'string') return null;

    let end = r.meetingEndDate;
    if (!end || typeof end !== 'string') {
        const d = new Date(start);
        if (!Number.isNaN(d.getTime())) {
            d.setHours(d.getHours() + 1);
            end = d.toISOString();
        } else {
            end = start;
        }
    }

    return {
        id: `meet-${id}`,
        title: String(r.title || 'Haftalık toplantı'),
        start,
        end,
        backgroundColor: FC_MEETING_BG,
        borderColor: FC_MEETING_BORDER,
        textColor: '#ffffff',
        extendedProps: {
            kind: 'weekly-meeting',
            location: r.location != null ? String(r.location) : '',
            meetingRoomName:
                r.meetingRoomName != null ? String(r.meetingRoomName) : '',
            source: 'meeting',
            sourceId: id,
        },
    };
}

/**
 * Kulüp etkinlikleri + haftalık toplantılar → FullCalendar `events` dizisi.
 * @param {string} accessToken
 * @returns {Promise<Record<string, unknown>[]>}
 */
export async function fetchCalendarFeedEvents(accessToken) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const [clubRows, meetingRows] = await Promise.all([
        fetchAllAuthPages(
            accessToken,
            '/clubevents/ClubEvent/GetAll',
            'Kulüp etkinlikleri alınamadı',
        ),
        fetchAllAuthPages(
            accessToken,
            '/meetings/api/Meeting',
            'Toplantılar alınamadı',
        ),
    ]);

    /** @type {Record<string, unknown>[]} */
    const events = [];

    for (const row of clubRows) {
        const e = mapClubEventToFullCalendar(row);
        if (e) events.push(e);
    }
    for (const row of meetingRows) {
        const e = mapMeetingToFullCalendar(row);
        if (e) events.push(e);
    }

    events.sort((a, b) =>
        String(a.start).localeCompare(String(b.start), 'en', {
            numeric: true,
        }),
    );

    return events;
}
